import * as React from 'react';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { environmentVars } from 'constants/services';
import { AnalyticsContext } from './AnalyticsContext';
import { FullstoryAnalyticsService } from './fullstory-analytics-service';

const createStubAnalyticsService = (): Bluebeam.Services.IAnalyticsService => {
  return {
    anonymize() {},
    consent(userConsents?: boolean) {},
    event(eventName: string, eventProperties: { [key: string]: any }) {},
    identify(uid: string, customVars?: Bluebeam.Analytics.Models.UserVars) {},
    isInitialized() {
      return false;
    },
    restart(): void {},
    shutdown() {},
    currentStateIs(
      state: 'unknown' | 'initializing' | 'active' | 'inactive'
    ): boolean {
      return state === 'unknown';
    },
    getCurrentState(): 'unknown' | 'initializing' | 'active' | 'inactive' {
      return 'unknown';
    },
  };
};

export const AnalyticsProvider: React.FC = ({ children }) => {
  const [analyticsService, setAnalyticsService] =
    React.useState<Bluebeam.Services.IAnalyticsService>(
      createStubAnalyticsService()
    );

  const { [FeatureFlag.Fullstory]: isFullstoryEnabled } = useFeatureFlags();

  const initializeFullstory =
    isFullstoryEnabled && environmentVars.FULLSTORY_ORGANIZATION_ID;

  React.useEffect(() => {
    if (initializeFullstory) {
      if (analyticsService.currentStateIs('unknown')) {
        FullstoryAnalyticsService.Init({
          orgId: environmentVars.FULLSTORY_ORGANIZATION_ID,
        }).then((service) => {
          setAnalyticsService(service);
        });
      }

      if (analyticsService.currentStateIs('inactive')) {
        analyticsService.restart();
      }
    }

    if (!initializeFullstory) {
      if (analyticsService.currentStateIs('active')) {
        analyticsService.shutdown();
      }
    }
  }, [initializeFullstory]);

  const renderChildren = initializeFullstory
    ? analyticsService && analyticsService.isInitialized()
    : true;

  return (
    <AnalyticsContext.Provider value={analyticsService}>
      {renderChildren && children}
    </AnalyticsContext.Provider>
  );
};
